import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap";

/**
 * Close-Job Supervisor Override modal.
 *
 * Legacy parity: frmSupvOv.vb (close job when pieces remaining or
 * nonconformities exist). Returns Number(empId) via onConfirm so the
 * parent passes it to CloseJobQuality as QualityClosedBy.
 *
 * Password verification: intentionally NOT included (product decision).
 *   The backend (InspectionController.CloseJobQuality) now validates that
 *   the supplied empId belongs to a Manager / HR Admin / IT Admin /
 *   President / President-CEO when Noncomformant=true (Phase 6 fix),
 *   surfacing the legacy "A supervisor is required to close this job."
 *   message if not — without re-introducing plaintext-password compare.
 *
 * UI-consistency rewrite using reactstrap. Props unchanged.
 */
const FrmSupvOv = ({
  open,
  pcsRemaining,
  nonConformities,
  onCancel,
  onConfirm,
}) => {
  const [empId, setEmpId] = useState("");

  useEffect(() => {
    if (!open) {
      setEmpId("");
    }
  }, [open]);

  const valid = useMemo(() => {
    const n = Number(empId);
    return Number.isInteger(n) && n > 0;
  }, [empId]);

  const safePcsRemaining = useMemo(
    () => Number(pcsRemaining || 0),
    [pcsRemaining]
  );
  const safeNonConformities = useMemo(
    () => Number(nonConformities || 0),
    [nonConformities]
  );

  const handleEmpIdChange = useCallback((e) => {
    setEmpId(e.target.value);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm?.(Number(empId));
  }, [onConfirm, empId]);

  return (
    <Modal isOpen={!!open} toggle={onCancel} centered>
      <ModalHeader toggle={onCancel}>
        <i className="ri-shield-keyhole-line align-bottom me-2 text-warning"></i>
        Supervisor Override
        <Badge color="light" className="ms-2 text-muted">
          Close Job
        </Badge>
      </ModalHeader>
      <ModalBody>
        <div className="text-muted small mb-3">
          <div>
            <strong>Pieces Not Inspected:</strong> {safePcsRemaining}
          </div>
          <div>
            <strong>Non Conformities:</strong> {safeNonConformities}
          </div>
        </div>

        <Form onSubmit={(e) => e.preventDefault()}>
          <FormGroup className="mb-2">
            <Label for="supv-empid">Supervisor Employee ID (Closed By)</Label>
            <Input
              id="supv-empid"
              type="number"
              inputMode="numeric"
              value={empId}
              onChange={handleEmpIdChange}
              placeholder="Enter supervisor employee ID"
              autoFocus
            />
          </FormGroup>
          <div className="form-text text-muted">
            A manager-level employee ID is required. The server verifies the
            ID against the role table before closing.
          </div>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" disabled={!valid} onClick={handleConfirm}>
          <i className="ri-stop-circle-line align-bottom me-1"></i>
          Override & Close Job
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(FrmSupvOv);
